import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { addProductToList, removeProductFromList } from '../firestore/products';

const UNSAVED_ID = "UNSAVED_ID";

export const productId = (shopId: string, productRef: string): string => {
  return btoa(`${shopId}-${productRef}`);
}

export interface Product {
  shopId: string;
  productRef: string;
  uid?: string;
}

export interface ProductList {
  name: string; // "Watch List"
  description: string; // "mijn lijstje met favoriete producten"
  products: Product[];
}

export type ProductListMap = Record<string, ProductList>;

export interface ProductsState {
  selectedListId: string;
  lists: ProductListMap; // listId / ProductList
}

export const callAddToList = createAsyncThunk<
  void,
  { uid: string | null, listId: string | null, shopId: string, productRef: string },
  {
    rejectValue: Error;
  }
>(
  'products/callAddToList',
  async ({ uid, listId, shopId, productRef }, thunkAPI) => {
    const product: Product = {
      productRef: productRef,
      shopId: shopId,
      uid: uid,
    } as Product;
    if (!uid) { // not logged in
      thunkAPI.dispatch(productsSlice.actions.addProduct(
        {
          listId: UNSAVED_ID,
          product: product,
        }))
    } else if (listId) {
      await addProductToList(uid, listId, product);
    } else {
      console.warn(`unexpected problem: add product to listId==null`);
    }
  }
);

export const callRemoveFromList = createAsyncThunk<
  void,
  { uid: string | null, listId: string | null, shopId: string, productRef: string },
  {
    rejectValue: Error;
  }
>(
  'products/callRemoveFromList',
  async ({ uid, listId, shopId, productRef }, thunkAPI) => {
    const product: Product = {
      productRef: productRef,
      shopId: shopId,
      uid: uid,
    } as Product;
    if (!uid) { // not logged in
      thunkAPI.dispatch(productsSlice.actions.removeProduct(
        {
          listId: UNSAVED_ID,
          product: product,
        }))
    } else if (listId) {
      await removeProductFromList(uid, listId, product);
    } else {
      console.warn(`unexpected problem: add product to listId==null`);
    }
  }
);

const INITIAL_STATE: ProductsState = {
  selectedListId: UNSAVED_ID,
  lists: {
    UNSAVED_ID: {
      description: "Mijn favoriete producten",
      name: "Watch list",
      products: [] as Product[]
    } as ProductList
  },
};

export const productsSlice = createSlice({
  name: 'products',
  initialState: INITIAL_STATE,
  reducers: {
    // setLists: (state: ProductsState, action: PayloadAction<ProductListMap>) => {
    //   state.lists = action.payload;
    // },
    addOrUpdateList: (state: ProductsState, action: PayloadAction<{ id: string, list: ProductList }>) => {
      state.lists[action.payload.id] = action.payload.list;
      // if there are saved lists, we delete the unsaved one from the INITIAL_STATE
      if (action.payload.id !== UNSAVED_ID) {
        // remove UNSAVED_ID and set selection
        delete state.lists[UNSAVED_ID];
        if (state.selectedListId === UNSAVED_ID) {
          state.selectedListId = action.payload.id;
        }

      }
    },
    removeList: (state: ProductsState, action: PayloadAction<{ id: string }>) => {
      delete state.lists[action.payload.id];
    },
    // only used for unsaved list / not logged on
    addProduct: (state: ProductsState, action: PayloadAction<{ listId: string, product: Product }>) => {
      if (action.payload.listId in state.lists) {
        const productList = state.lists[action.payload.listId];
        const existingProductIndex = productList.products.findIndex(product =>
          product.shopId === action.payload.product.shopId && product.productRef === action.payload.product.productRef);
        if (existingProductIndex === -1) {
          productList.products.push(action.payload.product);
          console.log(`${action.payload.product.shopId}/${action.payload.product.productRef} added to list`);
        } else {
          console.warn(`addProduct: ${action.payload.product.shopId}/${action.payload.product.productRef} already in list`);
        }
      }
    },
    // only used for unsaved list / not logged on
    removeProduct: (state: ProductsState, action: PayloadAction<{ listId: string, product: Product }>) => {
      if (action.payload.listId in state.lists) {
        const productList = state.lists[action.payload.listId];
        const existingProductIndex = productList.products.findIndex(product =>
          product.shopId === action.payload.product.shopId && product.productRef === action.payload.product.productRef);
        if (existingProductIndex === -1) {
          console.warn(`removeProduct: ${action.payload.product.shopId}/${action.payload.product.productRef} not in list`);
        } else {
          productList.products.splice(existingProductIndex, 1);
        }
      }
    }
  }
});

export const { addOrUpdateList, removeList } = productsSlice.actions;

/**
 * return true if the product is in the currently selected list, otherwise false;
 * @param state 
 * @param shopId 
 * @param productRef 
 * @returns 
 */
export const isInlist = (state: RootState, shopId: string, productRef: string): boolean => {
  const list = state.products.lists[state.products.selectedListId];
  if (list) {
    const existingProductIndex = list.products.findIndex(product =>
      product.shopId === shopId && product.productRef === productRef);
    return (existingProductIndex >= 0);
  } else {
    return false;
  }

}

export const getSelectedListId = (state: RootState): string => state.products.selectedListId;
export const getSelectedList = (state: RootState): ProductList => state?.products?.lists[state.products.selectedListId];
export const getSelectedListCount = (state: RootState): number => state?.products?.lists[state.products.selectedListId]?.products?.length || 0;
export const getSelectedListName = (state: RootState): string => state?.products?.lists[state.products.selectedListId]?.name || "";
export const getList = (state: RootState, listId: string | null): ProductList | null => listId && state?.products?.lists[listId] ? state?.products?.lists[listId] : null;
export const productsReducer = productsSlice.reducer;
